import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Card, Button } from '../../components/ui';
import { Icon } from '../../components/icons';
import { BRAND, getThemeColors, DENSITY_VALUES } from '../../lib/theme';
import {
  v1Users,
  v1Admins, sortAdminsByFirstName,
  v1UserToAnfrage,
  v1UserToClient,
  v2Phases,
  v2Users,
  v2AnfrageAbbruch,
  v2Wiedervorlage,
  phaseRuleErrors,
} from '../../lib/v1';
import type { V1Admin, V1User, PbPhase } from '../../lib/v1';
import { pbList } from '../../lib/pbList';
import type { Anfrage, Density } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import { ClientDetail, type DrawerTab } from '../pflege/PflegeClientDetail';
import { toast } from '../../lib/toast';
import { formatDate } from '../../lib/dateFormat';
import {
  ANFRAGEN_PER_PAGE, ANFRAGEN_SORT_FIELDS,
  ANFRAGEN_TABLE_HEADERS, kontoToPhaseIdx, pbRefFromUser, pbHref,
  wiedervorlageRowTone,
} from './anfragenConstants';
import type { PbRef } from './anfragenConstants';
import { CreateAnfrageDrawer } from './CreateAnfrageDrawer';
import { PbProfileLink, PbNotesButton } from './PbProfileLink';
import { AnfrageActionsMenu, AnfrageAbbruchDialog } from './AnfrageAbbruchDialog';
import { ColumnPicker } from '../../components/ColumnPicker';
import { AdminMultiFilter } from '../../components/AdminMultiFilter';
import { readAssigneeFilter, writeAssigneeFilter } from '../../lib/assigneeFilterPref';
import { useColumnPreferences } from '../../lib/useColumnPreferences';
import { rowLinkProps } from '../../lib/openLink';

// Batch-12: alle Tabellen-Spalten (für Column-Picker-Defaults).
const ANFRAGEN_ALL_COLS = ANFRAGEN_TABLE_HEADERS.map(h => h.key);

interface AnfragenPageProps {
  dark: boolean;
  density: Density;
  /**
   * Öffnet die VOLLE PB-Detailansicht (dieselbe wie die Pflege-Seite) für den
   * PB hinter einer Anfrage. `userId` = users.id des Pflegeempfängers — seit
   * 23.09.2026 die führende ID, auch in der URL. Wird in App.tsx auf
   * `navigate('pflege', userId)` gemappt.
   */
  onOpenPb?: (userId: number) => void;
}

export function AnfragenPage({ dark, density, onOpenPb }: AnfragenPageProps) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  const d = DENSITY_VALUES[density];

  const { user: currentUser } = useAuth();
  const { hasPermission } = usePermissions();
  const canAssign = hasPermission('anfragen.assign');
  const canCancel = hasPermission('users.write');
  const currentAdminId = currentUser?.id ?? null;

  const [items, setItems] = useState<Anfrage[]>([]);
  // Anfrage-ID → Familienkonto/Profil-Referenz (für Detail-Öffnen + Deep-Link).
  const [pbRefs, setPbRefs] = useState<Record<string, PbRef>>({});
  // Anfrage-ID → raw V1User (für PB-Drawer + konto-basierte Phase-Zuordnung).
  const [rawUsersMap, setRawUsersMap] = useState<Record<string, V1User>>({});
  // Phases from settings (dynamic board columns).
  const [phases, setPhases] = useState<PbPhase[]>([]);
  // PB-Schnellansicht-Drawer (ClientDetail, wie in der Pflege-Liste) —
  // Klick auf eine Karte/Zeile öffnet ihn mit dem Raw-User.
  const [drawerUser, setDrawerUser] = useState<V1User | null>(null);
  const [drawerTab, setDrawerTab] = useState<DrawerTab>('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  // Anfrage-Abbruch: Ziel-Anfrage des geöffneten Abbruch-Dialogs (oder null).
  const [abbruchTarget, setAbbruchTarget] = useState<{ id: string; name: string } | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [tab, setTab] = useState<'board' | 'table'>('board');
  const [dragId, setDragId] = useState<string | null>(null);
  // Debounced Suchbegriff → serverseitige Suche; Pagination über page/last_page.
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  // Sortierung (serverseitig). Default: neueste zuerst (id, absteigend).
  const [sortField, setSortField] = useState('id');
  const [sortProfile, setSortProfile] = useState(false);
  const [sortOrder, setSortOrder] = useState<1 | -1>(-1);

  // All admins (for the multi-select assignee filter)
  const [admins, setAdmins] = useState<V1Admin[]>([]);
  // Selected admin ids for the assignee filter. Default: [currentAdminId] if available, else [] = show all.
  const [assignedFilter, setAssignedFilter] = useState<number[]>([]);
  // Batch-13 — Wiedervorlage-Chip: „fällig" (heute + überfällig). Läuft
  // bewusst über einen eigenen v2-Endpunkt statt eines Client-Filters über
  // die geladene Seite — sonst würde der Chip nur die 50 aktuell geladenen
  // Zeilen durchsuchen statt global über alle Anfragen zu filtern.
  const [onlyFaellig, setOnlyFaellig] = useState(false);
  // Track initialized — set filter to current admin once it's known
  const [filterInitialized, setFilterInitialized] = useState(false);

  // Batch-12: Spalten ein-/ausblendbar (persistiert je Admin) + Client-
  // Sortierung für abgeleitete Spalten ohne serverseitiges Sortierfeld.
  const [visibleCols, setVisibleCols] = useColumnPreferences('anfragen.table', currentAdminId, ANFRAGEN_ALL_COLS);
  const [clientSort, setClientSort] = useState<{ key: string; dir: 1 | -1 } | null>(null);

  const toggleSort = (sortKey: string) => {
    const sf = ANFRAGEN_SORT_FIELDS[sortKey];
    if (!sf) {
      // Abgeleitete Spalte → client-seitig über die geladenen Zeilen sortieren.
      setClientSort(cs => cs?.key === sortKey ? { key: sortKey, dir: cs.dir === 1 ? -1 : 1 } : { key: sortKey, dir: 1 });
      return;
    }
    setClientSort(null);
    if (sortField === sf.field && sortProfile === !!sf.profile) {
      setSortOrder(o => (o === 1 ? -1 : 1));
    } else {
      setSortField(sf.field);
      setSortProfile(!!sf.profile);
      setSortOrder(1);
    }
  };


  // Load phases from settings on mount.
  useEffect(() => {
    v2Phases.board().then(setPhases).catch(() => { /* silent — board falls back to empty */ });
  }, []);

  // Load admins on mount for the assignee dropdown.
  // NB: admins are a SEPARATE table from users — their `konto` is NOT the user
  // "4 = Aktiv" convention (admin konto ∈ {0,1,3}), so filtering by konto===4
  // yields an empty list. List ALL admins unfiltered (P-01: PflegePage, das
  // dieses Muster vorgab, wurde entfernt — /pflege rendert FamilienkontoListPage;
  // das "Zugewiesen-an"-Muster bleibt hier bestehen) so the dropdown actually
  // populates. Sorted by name.
  useEffect(() => {
    let alive = true;
    v1Admins.list()
      .then(list => {
        if (!alive) return;
        const sorted = sortAdminsByFirstName(list);
        setAdmins(sorted);
      })
      .catch(() => { /* silent */ });
    return () => { alive = false; };
  }, []);

  // Once we know the current admin id, set the default filter to "me"
  useEffect(() => {
    if (!filterInitialized && currentAdminId !== null) {
      // Gespeicherte Auswahl (pro Admin, localStorage) hat Vorrang; [] = „alle".
      setAssignedFilter(readAssigneeFilter('anfragen', currentAdminId) ?? [currentAdminId]);
      setFilterInitialized(true);
    }
  }, [currentAdminId, filterInitialized]);

  // Debounce die Sucheingabe (350 ms) in `query`.
  useEffect(() => {
    const id = setTimeout(() => setQuery(search.trim()), 350);
    return () => clearTimeout(id);
  }, [search]);

  // Load PBs (= inquiries). Backend has no "leads" table — PB users ARE the
  // inquiry pipeline.
  //
  // Board-Ansicht: lädt PRO PHASE (filters[phase]) die neuesten Einträge —
  // damit jede Spalte vollständig gefüllt ist, unabhängig davon, in welcher
  // globalen Seite die User liegen würden. Tabellen-Ansicht: klassisch
  // paginiert (Seite 1 + „Mehr laden").
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const applyUsers = (users: V1User[], total: number, currentPage: number, last: number) => {
          if (!alive) return;
          setItems(users.map(v1UserToAnfrage));
          const refs: Record<string, PbRef> = {};
          const rawMap: Record<string, V1User> = {};
          users.forEach(u => {
            refs[`A-${u.id}`] = pbRefFromUser(u);
            rawMap[`A-${u.id}`] = u;
          });
          setPbRefs(refs);
          setRawUsersMap(rawMap);
          setPage(currentPage);
          setLastPage(last);
          setTotal(total);
        };

        if (onlyFaellig) {
          // Globale Suche über ALLE fälligen/überfälligen Anfragen (nicht nur
          // die aktuell geladene Seite) — s. Kommentar oben am State.
          const res = await v2Wiedervorlage.faellig('due', { page: 1, per_page: ANFRAGEN_PER_PAGE });
          applyUsers(res.data, res.total, res.current_page, res.last_page);
        } else if (tab === 'board' && phases.length > 0) {
          // Eine Anfrage-Liste je Phase parallel laden (je max. 200).
          // Batch-13 Paket C — scope=anfragen (statt des eingefrorenen v1-
          // Endpunkts): identisch zur Kunde/Anfrage-Definition auf dem
          // Server, dadurch verschwindet eine Karte automatisch vom Board,
          // sobald sie in der letzten Phase eine bestätigte Betreuung erhält.
          const results = await Promise.all(phases.map(ph =>
            pbList.list('anfragen', {
              page: 1, perPage: 200,
              filters: { ...(query ? { search: query } : {}), phase: ph.id, excludeCancelled: 1 },
              orderBy: { field: sortField, profile: sortProfile }, order: sortOrder,
            }),
          ));
          if (!alive) return;
          const seen = new Set<number>();
          const merged: V1User[] = [];
          results.forEach(res => {
            (res.data as V1User[]).forEach(u => {
              if (!seen.has(u.id)) { seen.add(u.id); merged.push(u); }
            });
          });
          const totalAll = results.reduce((s, r) => s + r.total, 0);
          applyUsers(merged, totalAll, 1, 1); // Board: kein „Mehr laden"
        } else {
          const res = await pbList.list('anfragen', {
            page: 1, perPage: ANFRAGEN_PER_PAGE,
            // Abgebrochene Anfragen (negative Phase) bleiben draußen.
            filters: { ...(query ? { search: query } : {}), excludeCancelled: 1 },
            orderBy: { field: sortField, profile: sortProfile }, order: sortOrder,
          });
          applyUsers(res.data as V1User[], res.total, res.current_page, res.last_page);
        }
      } catch (e) {
        if (!alive) return;
        setError(e instanceof Error ? e.message : tr('anfragen.error.load'));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [query, sortField, sortProfile, sortOrder, reloadKey, tab, phases, onlyFaellig]);

  // Hängt die nächste Seite an (behält Suche bei).
  const loadMore = async () => {
    if (loadingMore || page >= lastPage) return;
    setLoadingMore(true);
    try {
      const res = onlyFaellig
        ? await v2Wiedervorlage.faellig('due', { page: page + 1, per_page: ANFRAGEN_PER_PAGE })
        : await pbList.list('anfragen', {
            page: page + 1, perPage: ANFRAGEN_PER_PAGE,
            filters: { ...(query ? { search: query } : {}), excludeCancelled: 1 },
            orderBy: { field: sortField, profile: sortProfile }, order: sortOrder,
          });
      setItems(prev => [...prev, ...res.data.map(v1UserToAnfrage)]);
      setPbRefs(prev => {
        const next = { ...prev };
        res.data.forEach(u => { next[`A-${u.id}`] = pbRefFromUser(u); });
        return next;
      });
      setRawUsersMap(prev => {
        const next = { ...prev };
        res.data.forEach(u => { next[`A-${u.id}`] = u; });
        return next;
      });
      setPage(res.current_page);
      setLastPage(res.last_page);
      setTotal(res.total);
    } catch (e) {
      setError(e instanceof Error ? e.message : tr('anfragen.error.load'));
    } finally {
      setLoadingMore(false);
    }
  };

  // Klick auf eine Karte/Zeile (oder das Notizen-Icon): öffnet die
  // PB-Schnellansicht (ClientDetail) wie in der Pflege-Liste, wahlweise
  // direkt auf dem Notizen-Tab.
  const openPb = (anfrageId: string, tab: DrawerTab = 'overview') => {
    const raw = rawUsersMap[anfrageId];
    if (!raw) return;
    setDrawerTab(tab);
    setDrawerUser(raw);
  };

  // Bearbeiten-Button: navigiert direkt zur vollen Familienkonto-Detailseite
  // (wie der Bearbeiten-Button in der Pflegebedürftigen-Liste), statt den
  // Karten-Drawer zu öffnen.
  const openFamilienkonto = (anfrageId: string) => {
    const ref = pbRefs[anfrageId];
    if (onOpenPb && ref?.accountId != null && ref?.userId != null) onOpenPb(ref.userId);
  };

  // Suche läuft serverseitig; assignee filter als clientseitige Verfeinerung
  // über die geladenen Seiten.
  // Phase-1-Anfragen (noch keine Phase gesetzt) und noch nicht zugewiesene
  // Anfragen sind für alle sichtbar — sonst verschwindet eine unzugewiesene
  // Karte beim Verschieben aus Phase 1 sofort vom Board.
  const filtered = items.filter(i => {
    // Phase 1: immer für alle sichtbar (kein assigned-Filter)
    const raw = rawUsersMap[i.id];
    const isPhase1 = !raw?.phase || (phases.length > 0 && raw.phase === phases[0]?.id);
    if (isPhase1) return true;
    if (i.assigned_to == null) return true;
    if (assignedFilter.length > 0 && !assignedFilter.includes(i.assigned_to)) return false;
    return true;
  });

  // Batch-12: Tabellen-Zeilen — bei aktiver Client-Sortierung die geladenen
  // Zeilen nach der abgeleiteten Spalte sortieren (need/budget/assigned_to).
  const clientSortValue = (r: Anfrage, key: string): string | number => {
    switch (key) {
      case 'need': return r.need ?? '';
      case 'budget': return Number(String(r.budget).replace(/[^\d]/g, '')) || 0;
      case 'assigned_to': return r.assigned_to_name ?? '';
      default: return '';
    }
  };
  const tableRows = clientSort
    ? [...filtered].sort((a, b) => {
        const va = clientSortValue(a, clientSort.key);
        const vb = clientSortValue(b, clientSort.key);
        const cmp = typeof va === 'number' && typeof vb === 'number'
          ? va - vb
          : String(va).localeCompare(String(vb), 'de');
        return cmp * clientSort.dir;
      })
    : filtered;
  const tableCols = ANFRAGEN_TABLE_HEADERS.filter(h => visibleCols.includes(h.key));
  const showCol = (k: string) => visibleCols.includes(k);


  // Drag&Drop: Karte in eine andere Phasen-Spalte ziehen → persistiert die
  // neue Phase via PATCH /admin/users/{id}/phase. Optimistisches Update mit
  // Rollback bei Fehler; bei E_PHASE_RULES erscheinen die unerfüllten
  // Bedingungen als Toast.
  const handleDropOnPhase = async (targetPhase: PbPhase) => {
    const id = dragId;
    setDragId(null);
    if (!id) return;
    const raw = rawUsersMap[id];
    if (!raw || raw.phase === targetPhase.id) return;
    const rawId = Number(id.replace('A-', ''));
    setRawUsersMap(prev => ({ ...prev, [id]: { ...raw, phase: targetPhase.id } }));
    try {
      await v2Users.setPhase(rawId, { phase: targetPhase.id });
    } catch (e) {
      setRawUsersMap(prev => ({ ...prev, [id]: raw }));
      const unmet = phaseRuleErrors(e);
      if (unmet) {
        toast(unmet, 'warning', tr('anfragen.phase.rules_unmet_title'));
      }
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: d.gap, height: '100%' }}>
      {error && (
        <Card dark={dark} style={{ padding: 12, borderColor: '#f04438' }}>
          <div style={{ fontSize: 13, color: '#b42318', fontWeight: 500 }}>
            {tr('anfragen.error.load')}: {error}
          </div>
        </Card>
      )}


      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', background: t.card, border: `1px solid ${t.border}`, borderRadius: 9, padding: 2 }}>
          {[
            { k: 'board' as const, l: tr('anfragen.list.view.board'), Ic: Icon.Board },
            { k: 'table' as const, l: tr('anfragen.list.view.table'), Ic: Icon.List },
          ].map(o => (
            <button key={o.k} onClick={() => setTab(o.k)} style={{
              padding: '6px 12px', borderRadius: 7,
              background: tab === o.k ? '#fce7f4' : 'transparent',
              color: tab === o.k ? BRAND : t.muted,
              border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 500,
              display: 'inline-flex', alignItems: 'center', gap: 6,
              fontFamily: 'Outfit, sans-serif',
            }}>
              <o.Ic c={tab === o.k ? BRAND : t.muted}/>{o.l}
            </button>
          ))}
        </div>

        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          <span style={{ position: 'absolute', left: 10 }}><Icon.Search c={t.placeholder} size={14}/></span>
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder={tr('common.search.placeholder')}
            style={{ fontSize: 12, border: `1px solid ${t.border}`, borderRadius: 8, padding: '7px 12px 7px 32px', width: 220, background: t.inputBg, color: t.text, fontFamily: 'Outfit,sans-serif', outline: 'none' }}
          />
        </div>

        {/* Batch-13 — Wiedervorlage-Chip: heute fällig + überfällig, global (v2). */}
        <button
          type="button"
          onClick={() => setOnlyFaellig(v => !v)}
          title={tr('anfragen.filter.wiedervorlage_faellig_hint') || 'Nur Anfragen mit fälliger/überfälliger Wiedervorlage'}
          style={{
            fontSize: 12, fontWeight: 600, border: `1px solid ${onlyFaellig ? '#b42318' : t.border}`,
            borderRadius: 8, padding: '7px 12px', cursor: 'pointer', fontFamily: 'Outfit,sans-serif',
            background: onlyFaellig ? '#fef3f2' : t.inputBg, color: onlyFaellig ? '#b42318' : t.text,
            display: 'inline-flex', alignItems: 'center', gap: 6,
          }}
        >
          <Icon.Bell c={onlyFaellig ? '#b42318' : t.muted}/>
          {tr('anfragen.filter.wiedervorlage_faellig') || 'Wiedervorlage fällig'}
        </button>

        {/* Batch-12: Spalten ein-/ausblenden (nur Tabellenansicht) */}
        {tab === 'table' && (
          <ColumnPicker
            dark={dark}
            columns={ANFRAGEN_TABLE_HEADERS.map(h => ({ id: h.key, label: tr(`anfragen.list.header.${h.key}`) }))}
            visible={visibleCols}
            onChange={setVisibleCols}
          />
        )}

        {/* Assignee multi-filter (gemeinsame Komponente mit der PB-Liste) */}
        <AdminMultiFilter
          dark={dark}
          admins={admins}
          value={assignedFilter}
          onChange={ids => {
            setAssignedFilter(ids);
            writeAssigneeFilter('anfragen', currentAdminId, ids);
          }}
          currentAdminId={currentAdminId}
        />

        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <Button variant="primary" dark={dark} size="sm" icon={<Icon.Plus/>} onClick={() => setCreateOpen(true)}>{tr('anfragen.action.new')}</Button>
        </div>
      </div>

      {/* Board view */}
      {tab === 'board' && (
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.max(phases.length, 1)}, 1fr)`, gap: 10, flex: 1, minHeight: 0, overflowX: 'auto' }}>
          {phases.map((phase, phaseIdx) => {
            const col = filtered.filter(i => {
              const raw = rawUsersMap[i.id];
              const userPhase = raw?.phase ?? 0;
              // Items with no phase set fall into column 0 (Phase 1).
              return userPhase === phase.id || (phaseIdx === 0 && !userPhase);
            });
            return (
              <div
                key={phase.id}
                onDragOver={e => e.preventDefault()}
                onDrop={() => void handleDropOnPhase(phase)}
                style={{ background: t.cardMuted, border: `1px solid ${t.border}`, borderRadius: 12, padding: 10, display: 'flex', flexDirection: 'column', minWidth: 200 }}
              >
                <div style={{ marginBottom: 10, paddingLeft: 4 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
                      <span style={{ width: 7, height: 7, borderRadius: '50%', background: phase.color, flexShrink: 0 }}/>
                      <span style={{ fontSize: 12, fontWeight: 600, color: t.heading }}>{phase.title}</span>
                    </div>
                    <span style={{ fontSize: 11, color: t.muted, background: t.card, borderRadius: 9999, padding: '1px 8px', border: `1px solid ${t.border}`, flexShrink: 0 }}>{col.length}</span>
                  </div>
                  {/* Batch-13 — Stage-Beschreibung: max. 2 Zeilen, Rest per Tooltip
                      (title-Attribut) — sprengt sonst die Spaltenköpfe bei langen Texten. */}
                  {phase.description && (
                    <div
                      title={phase.description}
                      style={{
                        fontSize: 10.5, color: t.muted, marginTop: 3, paddingLeft: 14,
                        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                        overflow: 'hidden', lineHeight: 1.35,
                      }}
                    >
                      {phase.description}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, overflowY: 'auto', flex: 1 }}>
                  {loading && col.length === 0 && (
                    <div style={{ fontSize: 11, color: t.muted, padding: '6px 4px' }}>{tr('common.loading_short')}</div>
                  )}
                  {col.map(item => (
                    <div
                      key={item.id}
                      draggable
                      onDragStart={() => setDragId(item.id)}
                      onDragEnd={() => setDragId(null)}
                      {...rowLinkProps(pbHref(pbRefs[item.id]), () => void openPb(item.id))}
                      data-wiedervorlage={wiedervorlageRowTone(item.wiedervorlage_am, dark) ? 'true' : undefined}
                      /* Wiedervorlage erreicht = gelb, überschritten = rot —
                         gleiche Markierung wie in der Tabellenansicht. */
                      style={{
                        background: wiedervorlageRowTone(item.wiedervorlage_am, dark)?.bg ?? t.card,
                        border: `1px solid ${wiedervorlageRowTone(item.wiedervorlage_am, dark)?.border ?? t.border}`,
                        borderRadius: 10, padding: 12, cursor: 'pointer',
                        opacity: dragId === item.id ? 0.5 : 1, transition: 'box-shadow 0.15s',
                      }}
                      onMouseEnter={e => (e.currentTarget as HTMLDivElement).style.boxShadow = dark ? '0 4px 10px rgba(0,0,0,0.3)' : '0 4px 10px -2px rgba(16,24,40,0.08)'}
                      onMouseLeave={e => (e.currentTarget as HTMLDivElement).style.boxShadow = 'none'}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                        <div style={{ fontSize: 10, color: t.muted, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                          {item.id}
                        </div>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <PbNotesButton href={pbHref(pbRefs[item.id])} dark={dark} onOpen={() => void openPb(item.id, 'notizen')} />
                          <PbProfileLink href={pbHref(pbRefs[item.id])} dark={dark} onOpen={() => openFamilienkonto(item.id)} />
                          {canCancel && (
                            <AnfrageActionsMenu dark={dark} onAbbrechen={() => setAbbruchTarget({ id: item.id, name: item.name })} />
                          )}
                        </div>
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: t.heading, marginBottom: 2 }}>{item.name}</div>
                      <div style={{ fontSize: 11, color: t.muted, display: 'flex', alignItems: 'center', gap: 5, marginBottom: 8 }}>
                        <Icon.MapPin c={t.muted}/>{item.city} · {item.care_level}
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 10, color: t.muted }}>
                        <span>{item.received}</span>
                        <span style={{ fontWeight: 600, color: t.text }}>{item.budget.split('–')[0]}</span>
                      </div>
                      {/* Wiedervorlage-Badge: erreicht = gelb, überschritten =
                          rot (vorher war beides rot). Farbe kommt aus
                          wiedervorlageRowTone, damit Badge, Kachelhintergrund
                          und Drawer denselben Ton benutzen. */}
                      {item.wiedervorlage_am && (() => {
                        const tone = wiedervorlageRowTone(item.wiedervorlage_am, dark);
                        return (
                          <div
                            title={item.wiedervorlage_notiz || undefined}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 4, marginTop: 6, fontSize: 10,
                              color: tone ? tone.text : t.muted, fontWeight: tone ? 600 : 400,
                            }}
                          >
                            <Icon.Bell c={tone ? tone.text : t.muted}/>
                            {formatDate(item.wiedervorlage_am)}
                          </div>
                        );
                      })()}
                      {/* Assignee row */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6, paddingTop: 6, borderTop: `1px solid ${t.borderSoft}` }}>
                        <span style={{ fontSize: 10, color: t.muted }}>
                          {item.assigned_to_name
                            ? (tr('anfragen.card.assigned_to', { name: item.assigned_to_name }) || `Zugewiesen: ${item.assigned_to_name}`)
                            : (tr('anfragen.card.unassigned') || 'Nicht zugewiesen')}
                        </span>
                        {canAssign && (item.assigned_to == null || item.assigned_to !== currentAdminId) && (
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              const rawId = Number(item.id.replace('A-', ''));
                              try {
                                const result = await v1Users.claimAnfrage(rawId);
                                setItems(prev => prev.map(it =>
                                  it.id === item.id
                                    ? { ...it, assigned_to: result.assigned_to, assigned_to_name: result.assigned_to_name }
                                    : it
                                ));
                                // Auto-Advance des Regelsystems: Karte in die neue Phase verschieben
                                if (result.phase != null) {
                                  setRawUsersMap(prev => prev[item.id]
                                    ? { ...prev, [item.id]: { ...prev[item.id], phase: result.phase, assigned_to: result.assigned_to } }
                                    : prev);
                                }
                              } catch { /* silent — toast could be added */ }
                            }}
                            style={{
                              fontSize: 10, fontWeight: 600,
                              background: BRAND, color: '#fff',
                              border: 'none', borderRadius: 6,
                              padding: '3px 8px', cursor: 'pointer',
                              fontFamily: 'Outfit,sans-serif',
                            }}
                          >
                            {tr('anfragen.action.claim') || 'Übernehmen'}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Table view */}
      {tab === 'table' && (
        <Card dark={dark} style={{ overflow: 'hidden', flex: 1, display: 'flex', flexDirection: 'column' }}>
          <div style={{ overflow: 'auto', flex: 1 }}>
            <table style={{ width: '100%', minWidth: 860, borderCollapse: 'collapse', fontSize: 13 }}>
              <thead style={{ position: 'sticky', top: 0, background: t.tableHeader, zIndex: 1 }}>
                <tr>
                  {tableCols.map(h => {
                    const sk = h.sortKey;
                    const sf = sk ? ANFRAGEN_SORT_FIELDS[sk] : undefined;
                    const serverActive = !!sf && sortField === sf.field && sortProfile === !!sf.profile;
                    const clientActive = !sf && clientSort?.key === h.key;
                    const active = serverActive || clientActive;
                    const dir = serverActive ? sortOrder : (clientSort?.dir ?? 1);
                    return (
                      <th
                        key={h.key}
                        onClick={() => toggleSort(sk ?? h.key)}
                        style={{ padding: `${d.rowY}px ${d.cellX}px`, textAlign: 'left', fontWeight: 600, fontSize: 11, color: active ? t.heading : t.muted, textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: `1px solid ${t.border}`, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                      >
                        <span>{tr(`anfragen.list.header.${h.key}`)}</span>
                        {active && <span style={{ marginLeft: 4 }}>{dir === 1 ? '▲' : '▼'}</span>}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {loading && filtered.length === 0 && (
                  <tr>
                    <td colSpan={tableCols.length} style={{ padding: 20, color: t.muted, fontSize: 12, textAlign: 'center' }}>
                      {tr('anfragen.list.loading')}
                    </td>
                  </tr>
                )}
                {!loading && filtered.length === 0 && (
                  <tr>
                    <td colSpan={tableCols.length} style={{ padding: 20, color: t.muted, fontSize: 12, textAlign: 'center' }}>
                      {tr('anfragen.list.empty')}
                    </td>
                  </tr>
                )}
                {tableRows.map(r => {
                  // Wiedervorlage erreicht = gelb, überschritten = rot. Die
                  // Markierung liegt auf der ganzen Zeile, damit fällige
                  // Anfragen beim Überfliegen der Liste auffallen — nicht nur
                  // im Badge der Wiedervorlage-Spalte (die ausgeblendet sein
                  // kann). Der Hover-Handler stellt sie danach wieder her.
                  const tone = wiedervorlageRowTone(r.wiedervorlage_am, dark);
                  const rowBg = tone ? tone.bg : 'transparent';
                  return (
                  <tr
                    key={r.id}
                    {...rowLinkProps(pbHref(pbRefs[r.id]), () => void openPb(r.id))}
                    data-wiedervorlage={tone ? 'true' : undefined}
                    style={{ borderBottom: `1px solid ${t.borderSoft}`, cursor: 'pointer', background: rowBg }}
                    onMouseEnter={e => (e.currentTarget as HTMLTableRowElement).style.background = t.hover}
                    onMouseLeave={e => (e.currentTarget as HTMLTableRowElement).style.background = rowBg}
                  >
                    {showCol('id') && <td style={{ padding: `${d.rowY}px ${d.cellX}px`, color: t.muted, fontWeight: 600, fontSize: 12 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {r.id}
                        <PbNotesButton href={pbHref(pbRefs[r.id])} dark={dark} onOpen={() => void openPb(r.id, 'notizen')} />
                        <PbProfileLink href={pbHref(pbRefs[r.id])} dark={dark} onOpen={() => openFamilienkonto(r.id)} />
                        {canCancel && (
                          <AnfrageActionsMenu dark={dark} onAbbrechen={() => setAbbruchTarget({ id: r.id, name: r.name })} />
                        )}
                      </div>
                    </td>}
                    {showCol('family') && <td style={{ padding: `${d.rowY}px ${d.cellX}px` }}>
                      <div style={{ fontWeight: 600, color: t.heading }}>{r.name}</div>
                      <div style={{ fontSize: 11, color: t.muted, marginTop: 1 }}>{r.contact}</div>
                    </td>}
                    {showCol('city') && <td style={{ padding: `${d.rowY}px ${d.cellX}px`, color: t.text }}>{r.city}</td>}
                    {showCol('need') && <td style={{ padding: `${d.rowY}px ${d.cellX}px`, color: t.text }}>{r.need}</td>}
                    {showCol('care_level') && <td style={{ padding: `${d.rowY}px ${d.cellX}px`, color: t.text }}>{tr('anfragen.list.pg_badge', { value: r.care_level.replace('Pflegegrad ', '') })}</td>}
                    {showCol('budget') && <td style={{ padding: `${d.rowY}px ${d.cellX}px`, color: t.heading, fontWeight: 500 }}>{r.budget}</td>}
                    {showCol('status') && <td style={{ padding: `${d.rowY}px ${d.cellX}px` }}>
                      {(() => {
                        const raw = rawUsersMap[r.id];
                        const idx = kontoToPhaseIdx(raw?.konto ?? 0, phases.length);
                        const ph = phases[idx];
                        return ph
                          ? <span style={{ fontSize: 11, fontWeight: 600, borderRadius: 6, padding: '2px 8px', background: ph.color + '22', color: ph.color, border: `1px solid ${ph.color}55` }}>{ph.title}</span>
                          : <Badge tone="neutral" dark={dark}>{r.stage}</Badge>;
                      })()}
                    </td>}
                    {showCol('received') && <td style={{ padding: `${d.rowY}px ${d.cellX}px`, color: t.muted, fontSize: 12 }}>{r.received}</td>}
                    {showCol('wiedervorlage') && <td style={{ padding: `${d.rowY}px ${d.cellX}px`, maxWidth: 160 }}>
                      {r.wiedervorlage_am ? (() => {
                        // Gleicher Ton wie Zeilenhintergrund, Kachel und Drawer
                        // (tone === undefined → künftiges Datum, neutral).
                        const cellTone = wiedervorlageRowTone(r.wiedervorlage_am, dark);
                        const color = cellTone ? cellTone.text : t.text;
                        return (
                          <div
                            title={r.wiedervorlage_notiz || undefined}
                            style={{ fontSize: 12, color, fontWeight: cellTone ? 600 : 400, display: 'flex', flexDirection: 'column', gap: 1 }}
                          >
                            <span>{formatDate(r.wiedervorlage_am)}</span>
                            {r.wiedervorlage_notiz && (
                              <span style={{ fontSize: 11, color: cellTone ? color : t.muted, fontWeight: 400, maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {r.wiedervorlage_notiz}
                              </span>
                            )}
                          </div>
                        );
                      })() : <span style={{ fontSize: 12, color: t.muted }}>—</span>}
                    </td>}
                    {showCol('assigned_to') && <td style={{ padding: `${d.rowY}px ${d.cellX}px` }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontSize: 12, color: t.muted }}>
                          {r.assigned_to_name || '—'}
                        </span>
                        {canAssign && (r.assigned_to == null || r.assigned_to !== currentAdminId) && (
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              const rawId = Number(r.id.replace('A-', ''));
                              try {
                                const result = await v1Users.claimAnfrage(rawId);
                                setItems(prev => prev.map(it =>
                                  it.id === r.id
                                    ? { ...it, assigned_to: result.assigned_to, assigned_to_name: result.assigned_to_name }
                                    : it
                                ));
                                if (result.phase != null) {
                                  setRawUsersMap(prev => prev[r.id]
                                    ? { ...prev, [r.id]: { ...prev[r.id], phase: result.phase, assigned_to: result.assigned_to } }
                                    : prev);
                                }
                              } catch { /* silent */ }
                            }}
                            style={{
                              fontSize: 10, fontWeight: 600,
                              background: BRAND, color: '#fff',
                              border: 'none', borderRadius: 6,
                              padding: '2px 7px', cursor: 'pointer',
                              fontFamily: 'Outfit,sans-serif',
                            }}
                          >
                            {tr('anfragen.action.claim') || 'Übernehmen'}
                          </button>
                        )}
                      </div>
                    </td>}
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Pagination-Footer: Zähler + "Mehr laden" (serverseitig, Board + Tabelle). */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', flexShrink: 0 }}>
        <span style={{ fontSize: 12, color: t.muted }}>
          {tr('anfragen.list.count', { shown: items.length, total })}
        </span>
        {page < lastPage && (
          <Button variant="secondary" size="sm" dark={dark} disabled={loadingMore} onClick={loadMore}>
            {loadingMore ? tr('anfragen.list.loading') : tr('anfragen.list.load_more')}
          </Button>
        )}
      </div>

      {/* Anfrage abbrechen — Grund wählen, dann fällt die Anfrage aus der Pipeline */}
      <AnfrageAbbruchDialog
        open={abbruchTarget !== null}
        dark={dark}
        scope="pb"
        anfrageName={abbruchTarget?.name}
        onClose={() => setAbbruchTarget(null)}
        onConfirm={async (grundId, beschreibung) => {
          if (!abbruchTarget) return;
          const targetId = abbruchTarget.id;
          const rawId = Number(targetId.replace('A-', ''));
          await v2AnfrageAbbruch.abbrechen(rawId, {
            grund_id: grundId,
            ...(beschreibung ? { beschreibung } : {}),
          });
          // Aus allen Listen-States entfernen — Anfrage ist abgebrochen.
          setItems(prev => prev.filter(it => it.id !== targetId));
          setRawUsersMap(prev => { const next = { ...prev }; delete next[targetId]; return next; });
          setPbRefs(prev => { const next = { ...prev }; delete next[targetId]; return next; });
          setTotal(prev => Math.max(0, prev - 1));
          setAbbruchTarget(null);
          toast(tr('anfragen.abbruch.success'), 'success');
        }}
      />

      {/* Neue Anfrage erstellen */}
      <CreateAnfrageDrawer
        open={createOpen}
        dark={dark}
        onClose={() => setCreateOpen(false)}
        onCreated={() => {
          setCreateOpen(false);
          setReloadKey(k => k + 1);
        }}
      />

      {/* PB-Schnellansicht-Drawer (ClientDetail) — wie in der Pflege-Liste */}
      {drawerUser && (
        <ClientDetail
          key={drawerUser.id}
          item={v1UserToClient(drawerUser)}
          raw={drawerUser}
          initialTab={drawerTab}
          dark={dark}
          onClose={() => setDrawerUser(null)}
          onSaved={u => {
            setRawUsersMap(prev => ({ ...prev, [`A-${u.id}`]: u }));
            setDrawerUser(u);
          }}
          onEdit={() => {
            const ref = pbRefs[`A-${drawerUser.id}`];
            if (onOpenPb && ref?.accountId != null && ref?.userId != null) onOpenPb(ref.userId);
            setDrawerUser(null);
          }}
        />
      )}
    </div>
  );
}
